import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loginAdmin } from '../api';
import '../css/AdminLogin.css';

const AdminLogin = ({ setIsAdmin }) => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
    if (error) setError(''); // Clear error on input change
  };

  const validateInputs = () => {
    if (!credentials.username.trim()) {
      setError('Please enter your username/email');
      return false;
    }
    if (!credentials.password) {
      setError('Please enter your password');
      return false;
    }
    if (credentials.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateInputs()) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await loginAdmin(credentials);

      // Check if login was successful
      if (result && result.token) {
        // Store auth data
        localStorage.setItem('adminToken', result.token);
        localStorage.setItem('adminUsername', result.username || credentials.username);
        localStorage.setItem('adminLoginTime', new Date().toISOString());

        // Set admin state
        setIsAdmin(true);

        // Redirect to dashboard
        navigate('/sec-pd-mgmt/dashboard');
      } else {
        // Handle failed login
        setError(result?.message || 'Invalid credentials. Please try again.');
      }
    } catch (err) {
      console.error('Login error:', err);

      // Provide specific error messages
      if (err.message.includes('Invalid login credentials')) {
        setError('Invalid username or password. Please check and try again.');
      } else if (err.message.includes('Network')) {
        setError('Network error. Please check your connection and try again.');
      } else if (err.message.includes('API')) {
        setError('Server error. Please try again later.');
      } else {
        setError(err.message || 'Login failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismissError = () => {
    setError('');
  };

  return (
    <div className="admin-login-page">
      <div className="container-fluid">
        <div className="row min-vh-100">
          {/* Left side - Branding */}
          <div className="col-md-6 d-none d-md-flex align-items-center justify-content-center bg-primary">
            <div className="text-white text-center px-4">
              <div className="mb-4">
                <i className="fas fa-building fa-5x mb-3"></i>
              </div>
              <h1 className="display-4 fw-bold mb-4">Prathamesh Developers</h1>
              <p className="lead mb-3">Admin Portal</p>
              <p className="fs-5">Manage your real estate business with ease and efficiency</p>
              
              <hr className="bg-white my-4" />
              
              <div className="mt-4">
                <p className="small">
                  <i className="fas fa-check me-2"></i>Complete Property Management
                </p>
                <p className="small">
                  <i className="fas fa-check me-2"></i>Customer Inquiry Tracking
                </p>
                <p className="small">
                  <i className="fas fa-check me-2"></i>Real-Time Analytics
                </p>
              </div>
            </div>
          </div>

          {/* Right side - Login Form */}
          <div className="col-md-6 d-flex align-items-center justify-content-center bg-light">
            <div className="login-form-container w-100" style={{ maxWidth: '400px' }}>
              <div className="text-center mb-4">
                <h2 className="fw-bold text-dark">Admin Login</h2>
                <p className="text-muted">Enter your credentials to access the admin panel</p>
              </div>

              {/* Error Alert */}
              {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  <span>{error}</span>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleDismissError}
                    aria-label="Close"
                  ></button>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* Username Field */}
                <div className="mb-3">
                  <label htmlFor="username" className="form-label fw-bold">
                    <i className="fas fa-user me-2"></i>Username or Email
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0">
                      <i className="fas fa-user text-primary"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      id="username"
                      name="username"
                      value={credentials.username}
                      onChange={handleChange}
                      placeholder="Enter your email"
                      required
                      autoFocus
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div className="mb-4">
                  <label htmlFor="password" className="form-label fw-bold">
                    <i className="fas fa-lock me-2"></i>Password
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0">
                      <i className="fas fa-lock text-primary"></i>
                    </span>
                    <input
                      type="password"
                      className="form-control border-start-0"
                      id="password"
                      name="password"
                      value={credentials.password}
                      onChange={handleChange}
                      placeholder="Enter your password"
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* Remember Me & Forgot Password */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="rememberMe"
                      disabled={isLoading}
                    />
                    <label className="form-check-label" htmlFor="rememberMe">
                      Remember me
                    </label>
                  </div>
                  <a href="#!" className="text-primary text-decoration-none small">
                    Forgot Password?
                  </a>
                </div>

                {/* Login Button */}
                <div className="d-grid mb-3">
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg fw-bold"
                    disabled={isLoading || !credentials.username || !credentials.password}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Logging in...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-sign-in-alt me-2"></i>
                        Login
                      </>
                    )}
                  </button>
                </div>

                {/* Footer Info */}
                <div className="text-center">
                  <small className="text-muted">
                    <i className="fas fa-shield-alt me-1"></i>
                    Secure login. Your credentials are encrypted.
                  </small>
                </div>
              </form>

              {/* Additional Info */}
              <div className="mt-4 pt-4 border-top">
                <p className="text-muted small text-center mb-0">
                  Having trouble? Contact support or check your email credentials.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
